import paramListProvider from '@/apiProviders/paramList.provider';
import jllLocalstorageManager from 'jll-localstorage-manager';
import Console from '@/shared/console';

const getDefaultIfNotValid = function (candidate, replacement) {
  if (candidate === null || candidate === undefined || candidate === []) {
    return replacement;
  } else {
    return candidate;
  }
};

const mapToDropdown = function (item) {
  return {
    name: item.ValueDesc,
    value: item.ValueCode,
    shortName: item.ShortName,
    valueOrder: item.ValueOrder,
  };
};

const warningNoValueDescFound = function (paramName, valueCode, instanceRol) {
  // Some values will probably never be found in a paramlist because they are default values,
  // and while the match against a paramlist is still tried, the warning should not be shown if there is no match
  const ignoredValues = ['-', '—', 'N/A'];
  if (!ignoredValues.some((a) => a === valueCode)) {
    Console.warn(`could not find ValueDesc: ${paramName} / ${valueCode} / ${instanceRol}`);
  }
};

const sortByValueOrder = function (a, b) {
  const aValueOrder = Number(a.valueOrder);
  const bValueOrder = Number(b.valueOrder);
  if (aValueOrder < bValueOrder || bValueOrder === null) {
    return -1;
  }
  if (aValueOrder > bValueOrder || aValueOrder === null) {
    return 1;
  }
  return 0;
};

const paramListModule = {
  namespaced: true,

  state: {
    paramLists: [
      /* This is an example of the structure of the paramlists */
      // { paramId: '100', paramCode: 'lorem_ipsum', langCode: 'en', instanceRol: 'INSTANCE_US', paramListEnum: [ { valueCode: '0001', ValueDesc: 'lorem ipsum', Shortname: 'li', ... } ] }
    ],
    vasParamListRel: [],

    // paramListItems: [
    //   // { codParam: '*', langCode: '*', dictPairs: [{ name: '*', value: '...' }] }
    // ],
    // paramListCombos: [
    //   // { paramId: -1, langCode: '*', values: [] }
    // ],

    loadingParamList: [
      // { id: '', promise: Promise() }
    ],

    loadingVASParamListRel: [
      // { id: '', promise: Promise() }
    ],

    comboDefaultItem: {
      value: null,
      name: 'All',
    },
  },

  getters: {
    /* Gets list of values based on paramId (like 543) */
    getParamListEnumByParamId:
      (state, getters, rootState, rootGetters) =>
      (paramId, langCodeParam, instanceRol = rootGetters['authModule/fullInstance']) => {
        const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);

        const foundParamList = state.paramLists.find((a) => a.paramId === paramId && a.langCode === langCode && a.instanceRol === instanceRol);

        if (foundParamList) {
          return foundParamList.paramListEnum;
        } else {
          return null;
        }
      },

    /* Gets list of values based on paramCode (like yesNoNaParam) */
    getParamListEnumByParamCode:
      (state, getters, rootState, rootGetters) =>
      (paramCode, langCodeParam, instanceRol = rootGetters['authModule/fullInstance']) => {
        const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);
        const foundParamList = state.paramLists.find((a) => a.paramCode === paramCode && a.langCode === langCode && a.instanceRol === instanceRol);

        if (foundParamList) {
          return foundParamList.paramListEnum;
        }

        return null;
      },

    /* Gets label based on paramId and valueCode (like 543, 0001) */
    getValueDescByParamId:
      (state, getters, rootState, rootGetters) =>
      (paramId, valueCode, langCodeParam, instanceRol = rootGetters['authModule/fullInstance']) => {
        const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);
        const foundParamListEnum = getters.getParamListEnumByParamId(paramId, langCode, instanceRol);

        if (foundParamListEnum) {
          const foundEnum = foundParamListEnum.find((enumEl) => enumEl.ValueCode === valueCode);

          if (foundEnum) {
            return foundEnum.ValueDesc;
          }
        }

        warningNoValueDescFound(paramId, valueCode, instanceRol);

        return valueCode;
      },

    /* Gets label based on paramCode and valueCode (like yesNoNaParam, 0001) */
    getValueDescByParamCode:
      (state, getters, rootState, rootGetters) =>
      (paramCode, valueCode, langCodeParam, instanceRol = rootGetters['authModule/fullInstance']) => {
        const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);
        const foundParamListEnum = getters.getParamListEnumByParamCode(paramCode, langCode, instanceRol);

        if (foundParamListEnum) {
          const foundEnum = foundParamListEnum.find((a) => a.ValueCode === valueCode);

          if (foundEnum) {
            return foundEnum.ValueDesc;
          }
        }

        warningNoValueDescFound(paramCode, valueCode, instanceRol);

        return '';
      },

    /* Gets shortName based on paramId and valueCode (like 543, 0001) */
    getShortNameByParamId:
      (state, getters, rootState, rootGetters) =>
      (paramId, valueCode, langCodeParam, instanceRol = rootGetters['authModule/fullInstance']) => {
        const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);

        const foundParamListEnum = getters.getParamListEnumByParamId(paramId, langCode, instanceRol);
        if (foundParamListEnum) {
          const foundEnum = foundParamListEnum.find((a) => a.ValueCode === valueCode);
          if (foundEnum) {
            return foundEnum.ShortName;
          }
        }
        warningNoValueDescFound(paramId, valueCode, instanceRol);

        return valueCode;
      },

    /* Gets shortName based on paramCode and valueCode (like yesNoNaParam, 0001) */
    getShortNameByParamCode:
      (state, getters, rootState, rootGetters) =>
      (paramCode, valueCode, langCodeParam, instanceRol = rootGetters['authModule/fullInstance']) => {
        const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);

        const foundParamListEnum = getters.getParamListEnumByParamCode(paramCode, langCode, instanceRol);
        if (foundParamListEnum) {
          const foundEnum = foundParamListEnum.find((a) => a.ValueCode === valueCode);
          if (foundEnum) {
            return foundEnum.ShortName;
          }
        }
        warningNoValueDescFound(paramCode, valueCode, instanceRol);
        return valueCode;
      },

    getDropdownByParamId:
      (state, getters, rootState, rootGetters) =>
      (paramId, langCodeParam, instanceRol = rootGetters['authModule/fullInstance']) => {
        const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);

        const paramListEnum = getters.getParamListEnumByParamId(paramId, langCode, instanceRol);
        if (paramListEnum) {
          return paramListEnum.map(mapToDropdown).sort(sortByValueOrder);
        } else {
          return null;
        }
      },

    getDropdownByParamCode:
      (state, getters, rootState, rootGetters) =>
      (paramCode, langCodeParam, instanceRol = rootGetters['authModule/fullInstance']) => {
        const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);
        const paramListEnum = getters.getParamListEnumByParamCode(paramCode, langCode, instanceRol);

        if (paramListEnum) {
          return paramListEnum.map(mapToDropdown).sort(sortByValueOrder);
        } else {
          return null;
        }
      },

    getComboDefaultItem(state) {
      return state.comboDefaultItem;
    },

    getVasParamListRel: (state, getters, rootState, rootGetters) => (typeInfo) => {
      const foundVasParamListRel = state.vasParamListRel.find((a) => a.typeInfo === typeInfo);

      if (foundVasParamListRel) {
        return foundVasParamListRel.vasParamListRel;
      } else {
        return null;
      }
    },
  },

  mutations: {
    /* new SetParamListItem & SetParamListComboItem */
    addParamList(state, newParamList) {
      const alreadyInStore = state.paramLists.find((paramList) => {
        const paramCodeIsDefined = newParamList.paramCode !== undefined;
        const paramIdIsDefined = newParamList.paramId !== undefined;
        const isSameParamCode = paramList.paramCode === newParamList.paramCode;
        const isSameParamId = paramList.paramId === newParamList.paramId;
        const isSameLangCode = paramList.langCode === newParamList.langCode;
        const isSameInstanceRol = paramList.instanceRol === newParamList.instanceRol;
        return ((paramCodeIsDefined && isSameParamCode) || (paramIdIsDefined && isSameParamId)) && isSameLangCode && isSameInstanceRol;
      });

      if (alreadyInStore === undefined) {
        state.paramLists.push(newParamList);
      }
    },

    addVasParamListRel(state, newVasParamListRel) {
      const alreadyInStore = state.vasParamListRel.find((vasParamListRel) => {
        const typeInfoIsDefined = newVasParamListRel.typeInfo !== undefined;
        const isSameTypeInfo = vasParamListRel.typeInfo === newVasParamListRel.typeInfo;
        return typeInfoIsDefined && isSameTypeInfo;
      });

      if (alreadyInStore === undefined) {
        state.vasParamListRel.push(newVasParamListRel);
      }
    },

    addParamListLoadingToState(state, payload) {
      state.loadingParamList.push(payload);
    },

    addVasParamListRelLoadingToState(state, payload) {
      state.loadingVASParamListRel.push(payload);
    },

    removeParamListLoadingToState(state, id) {
      state.loadingParamList = state.loadingParamList.filter((a) => a.id !== id);
    },

    removeVasParamListRelLoadingToState(state, id) {
      state.loadingVASParamListRel = state.loadingVASParamListRel.filter((a) => a.id !== id);
    },
  },

  actions: {
    /* new GetParamIdFromServer */
    loadParamListByParamId({ state, commit, rootGetters, getters }, paramId, langCodeParam) {
      const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);
      const instanceRol = rootGetters['authModule/fullInstance'];

      const foundThatThisParamListIsAlreadyBeingLoaded = state.loadingParamList.find((a) => a.id === paramId);

      if (foundThatThisParamListIsAlreadyBeingLoaded) {
        return foundThatThisParamListIsAlreadyBeingLoaded.promise;
      }

      const loadingPromise = new Promise((resolve, reject) => {
        const foundInStore = state.paramLists.find((paramList) => {
          return paramList.paramId === paramId && paramList.langCode === langCode && paramList.instanceRol === instanceRol;
        });

        if (foundInStore) {
          commit('removeParamListLoadingToState', paramId);
          resolve(foundInStore);
        } else {
          const env = rootGetters['authModule/instance'];
          const localStorageKey = `PixParamId-${paramId.toString()}_${env}_${langCode}`;
          const foundInLocalStorage = jllLocalstorageManager.getWithoutInstanceLang(localStorageKey);

          if (foundInLocalStorage) {
            commit('addParamList', foundInLocalStorage);
            commit('removeParamListLoadingToState', paramId);
            resolve(foundInLocalStorage);
          } else {
            paramListProvider.getParamListEnumByParamId(paramId, langCode, instanceRol).then(
              (data) => {
                if (data) {
                  const paramListEnum = data;
                  const loadedParamList = {
                    paramId,
                    langCode,
                    paramListEnum,
                    instanceRol,
                  };

                  jllLocalstorageManager.setWithoutInstanceLang(localStorageKey, loadedParamList, 1000 * 3600 * 24);
                  commit('addParamList', loadedParamList);
                  commit('removeParamListLoadingToState', paramId);
                  resolve(loadedParamList);
                } else {
                  commit('removeParamListLoadingToState', paramId);
                  reject(`GetParamListEnumByParamId - ${paramId}`);
                }
              },
              () => {
                commit('removeParamListLoadingToState', paramId);
                reject(`GetParamListEnumByParamId - ${paramId}`);
              }
            );
          }
        }
      });

      commit('addParamListLoadingToState', { id: paramId, promise: loadingPromise });
      return loadingPromise;
    },

    /* new GetCodParamFromServer */
    loadParamListByParamCode({ state, commit, rootGetters, getters }, paramCode, langCodeParam) {
      const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);
      const instanceRol = rootGetters['authModule/fullInstance'];

      const foundThatThisParamListIsAlreadyBeingLoaded = state.loadingParamList.find((a) => a.id === paramCode);

      if (foundThatThisParamListIsAlreadyBeingLoaded) {
        return foundThatThisParamListIsAlreadyBeingLoaded.promise;
      }

      const loadingPromise = new Promise((resolve, reject) => {
        const foundInStore = state.paramLists.find((paramList) => {
          return paramList.paramCode === paramCode && paramList.langCode === langCode;
        });

        if (foundInStore) {
          commit('removeParamListLoadingToState', paramCode);
          resolve(foundInStore);
        } else {
          const env = rootGetters['authModule/instance'];
          const localStorageKey = `PixCodParam-${paramCode}_${env}_${langCode}`;
          const foundInLocalStorage = jllLocalstorageManager.getWithoutInstanceLang(localStorageKey);

          if (foundInLocalStorage) {
            commit('addParamList', foundInLocalStorage);
            commit('removeParamListLoadingToState', paramCode);
            resolve(foundInLocalStorage);
          } else {
            paramListProvider.getParamListEnumByParamCode(paramCode, langCode, instanceRol).then(
              (data) => {
                if (data) {
                  const paramListEnum = data;
                  const loadedParamList = {
                    paramCode,
                    langCode,
                    paramListEnum,
                    instanceRol,
                  };

                  jllLocalstorageManager.setWithoutInstanceLang(localStorageKey, loadedParamList, 1000 * 3600 * 24);
                  commit('addParamList', loadedParamList);
                  commit('removeParamListLoadingToState', paramCode);
                  resolve(loadedParamList);
                } else {
                  commit('removeParamListLoadingToState', paramCode);
                  reject(`GetParamListEnumByParamCode - ${paramCode}`);
                }
              },
              () => {
                commit('removeParamListLoadingToState', paramCode);
                reject(`GetParamListEnumByParamCode - ${paramCode}`);
              }
            );
          }
        }
      });

      commit('addParamListLoadingToState', { id: paramCode, promise: loadingPromise });
      return loadingPromise;
    },

    loadVASParamListRel({ state, commit, rootGetters, getters }, typeInfo, langCodeParam) {
      const langCode = getDefaultIfNotValid(langCodeParam, rootGetters.langCode);
      const foundThatThisVASParamListRelIsAlreadyBeingLoaded = state.loadingVASParamListRel.find((a) => a.id === typeInfo);

      if (foundThatThisVASParamListRelIsAlreadyBeingLoaded) {
        return foundThatThisVASParamListRelIsAlreadyBeingLoaded.promise;
      }

      const loadingPromise = new Promise((resolve, reject) => {
        const foundInStore = state.vasParamListRel.find((vasParamListRel) => {
          return vasParamListRel.typeInfo === typeInfo;
        });

        if (foundInStore) {
          commit('removeVasParamListRelLoadingToState', typeInfo);
          resolve(foundInStore);
        } else {
          const env = rootGetters['authModule/instance'];
          const localStorageKey = `PixVasParamIdRel-${typeInfo.toString()}_${env}_${langCode}`;
          const foundInLocalStorage = jllLocalstorageManager.getWithoutInstanceLang(localStorageKey);

          if (foundInLocalStorage) {
            commit('addVasParamListRel', foundInLocalStorage);
            commit('removeVasParamListRelLoadingToState', typeInfo);
            resolve(foundInLocalStorage);
          } else {
            paramListProvider.VAS_GetParamListRel(typeInfo, rootGetters['authModule/fullInstance']).then(
              (data) => {
                if (data) {
                  const vasParamListRel = data;
                  const loadedVasParamListRel = {
                    typeInfo,
                    vasParamListRel,
                  };

                  jllLocalstorageManager.setWithoutInstanceLang(localStorageKey, loadedVasParamListRel, 1000 * 3600 * 24);
                  commit('addVasParamListRel', loadedVasParamListRel);
                  commit('removeVasParamListRelLoadingToState', typeInfo);
                  resolve(loadedVasParamListRel);
                } else {
                  commit('removeVasParamListRelLoadingToState', typeInfo);
                  reject(`GetVasParamListRelByTypeInfo - ${typeInfo}`);
                }
              },
              () => {
                commit('removeVasParamListRelLoadingToState', typeInfo);
                reject(`GetVasParamListRelByTypeInfo - ${typeInfo}`);
              }
            );
          }
        }
      });

      commit('addVasParamListRelLoadingToState', { id: typeInfo, promise: loadingPromise });
      return loadingPromise;
    },
  },
};

/* Legacy equivalences */
/* Actions */
// GetParamIdFromServer(a) --> loadParamListByParamId(a)
// GetCodParamFromServer(a) --> loadParamListByParamCode(a)

/* Mutations */
// SetParamListItem & SetParamListComboItem ~~> addParamList

/* Getters */
// valueFromParamListKey(a, b, isShortName) --> (if shortname === true)  getValueDescByParamId(a, b)
// valueFromParamListKey(a, b, isShortName) --> (if shortname === false) getShortNameByParamId(a, b)
// IsCodParamInState(a) --> !!getParamListEnumByParamCode(a)
// paramListValueFor(a, b) --> getValueDescByParamCode(b, a)
// paramListShortNameFor(a, b) --> getShortNameByParamCode(b, a)
// paramCodeListFor(a) --> getDropdownByParamId(a)
// IsParamIdInState(a) --> !!getParamListEnumByParamId(a)
// paramIdListFor(a) --> getDropdownByParamCode(a)

export default paramListModule;
